import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RefreshCw, Link2 } from "lucide-react";

const DISMISS_KEY = "fl_xero_reconnect_dismissed";

// Small, app-wide prompt shown once per session to admins whose Xero connection
// needs reauthorising to unlock Payroll NZ or because the sign-in expired.
export function XeroReconnectPrompt() {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let active = true;
    const CHECKED_KEY = "fl_xero_reconnect_checked_at";
    if (Date.now() - Number(sessionStorage.getItem(CHECKED_KEY) || 0) < 10 * 60 * 1000) return undefined;
    (async () => {
      try {
        const { data } = await api.get("/xero/reconnect-status", { __flSkipReadCache: true });
        if (!data?.needs_reconnect) sessionStorage.setItem(CHECKED_KEY, String(Date.now()));
        if (!active || !data?.needs_reconnect || !data?.can_reauthorise) return;
        if (sessionStorage.getItem(DISMISS_KEY)) return;
        setInfo(data);
        setOpen(true);
      } catch { /* prompt is best-effort; never blocks the app */ }
    })();
    return () => { active = false; };
  }, []);

  const dismiss = () => { sessionStorage.setItem(DISMISS_KEY, "1"); setOpen(false); };
  const reauthorise = () => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    window.location.href = `${base}/api/xero/reauthorise`;
  };

  if (!info) return null;
  const reasons = info.reasons || [];
  const both = reasons.includes("payroll") && reasons.includes("expired");

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="sm:max-w-md" data-testid="xero-reconnect-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#FF5F15]" /> Reconnect Xero
          </DialogTitle>
          <DialogDescription className="pt-1">
            {reasons.includes("expired") && !both && `Your Xero sign-in for ${info.tenant_name || "your organisation"} has expired, so syncing has paused.`}
            {reasons.includes("payroll") && !both && `Reauthorise Xero to switch on the new payroll hours sync for ${info.tenant_name || "your organisation"}.`}
            {both && `Your Xero connection for ${info.tenant_name || "your organisation"} needs reauthorising to switch on payroll hours sync and resume invoice syncing.`}
            {" "}It takes one step — you don't need to disconnect, and your mappings, settings and history are kept.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={dismiss} data-testid="xero-reconnect-later">Remind me later</Button>
          <Button onClick={reauthorise} data-testid="xero-reconnect-now" className="bg-[#FF5F15] hover:bg-[#E65100] text-white">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Reauthorise Xero
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
